import { Loader } from '@googlemaps/js-api-loader';
import { keyBy } from 'es-toolkit';
import { onMounted } from 'vue';

import type { Address, AddressOption } from '../../../types/address';

/**
 * Parses an array of address components from the Google Maps API into an Address object
 * @see https://developers.google.com/maps/documentation/javascript/geocoding#GeocodingAddressTypes
 * @param addressComponents An array of address components from the Google Maps API
 * @returns An Address object
 */
function parseAddressComponents(
  addressComponents: Array<google.maps.GeocoderAddressComponent>,
): Omit<Address, 'lat' | 'long' | 'placeId'> {
  const components = keyBy(addressComponents, (c) => c.types[0]);

  return {
    street_address:
      [components['street_number']?.['long_name'], components['route']?.['short_name']].filter(Boolean).join(' ') ||
      undefined,
    extended_address: components['premise']?.['long_name'] || components['subpremise']?.['long_name'],
    state: components['administrative_area_level_1']?.['short_name'],
    postal_code: components['postal_code']?.['long_name'],
    city:
      components['locality']?.['long_name'] ||
      components?.['administrative_area_level_2']?.['long_name'] ||
      components?.['sublocality_level_1']?.['long_name'] ||
      components?.['sublocality']?.['long_name'],
    country: components['country']?.['short_name'],
  };
}

export default function useGoogleMaps(apiKey: string) {
  let autocompleteService: google.maps.places.AutocompleteService | null = null;
  let placesService: google.maps.places.PlacesService | null = null;

  function getPlacePredictions(query: string): Promise<Array<AddressOption>> {
    return new Promise((resolve, reject) => {
      if (!autocompleteService) {
        // eslint-disable-next-line prefer-promise-reject-errors
        reject({ isGoogleMapsError: false, message: 'Autocomplete service not initialized' });
        return;
      }

      autocompleteService.getPlacePredictions({ input: query }, (predictions, status) => {
        if (status !== google.maps.places.PlacesServiceStatus.OK) {
          // eslint-disable-next-line prefer-promise-reject-errors
          reject({
            isGoogleMapsError: true,
            message: `Failed to get query predictions with status ${status}`,
            status,
          });
          reject(status);
          return;
        }

        const options = (predictions || []).map(({ place_id, description }) => ({
          id: place_id,
          name: description,
        }));

        resolve(options);
      });
    });
  }

  function getPlaceDetails(placeId: string): Promise<Address> {
    return new Promise((resolve, reject) => {
      if (!placesService) {
        // eslint-disable-next-line prefer-promise-reject-errors
        reject({ isGoogleMapsError: false, message: 'Places service not initialized' });
        return;
      }

      placesService.getDetails({ placeId, fields: ['address_components', 'geometry'] }, (placeResult, status) => {
        if (status !== google.maps.places.PlacesServiceStatus.OK) {
          // eslint-disable-next-line prefer-promise-reject-errors
          reject({
            isGoogleMapsError: true,
            message: `Failed to get place details with status ${status}`,
            status,
          });
          return;
        }

        const partialAddress = parseAddressComponents(placeResult?.address_components || []);

        const address: Address = {
          ...partialAddress,
          lat: placeResult?.geometry?.location?.lat(),
          long: placeResult?.geometry?.location?.lng(),
          place_id: placeId,
        };

        resolve(address);
      });
    });
  }

  onMounted(() => {
    new Loader({
      apiKey,
      version: 'weekly',
      libraries: ['places'],
    })
      .importLibrary('places')
      .then((placesLib) => {
        autocompleteService = new placesLib.AutocompleteService();
        placesService = new placesLib.PlacesService(document.createElement('div'));
      });
  });

  return { getPlaceDetails, getPlacePredictions };
}

export type { Address, AddressOption };
